<template>
    <v-menu class="select-menu" :model-value="isOpen" width="400px" max-height="550px" location="end" target="parent"
        scroll-strategy="block" :close-on-content-click="false" @after-leave="resetState">
        <rb-loader :contained="true" :generic="true" v-model="isLoading" />
        <v-card class="d-flex flex-column" style="max-height: 500px;">
            <v-card-title class="header d-flex justify-space-between align-center">
                <p>{{ title }}</p>
                <v-btn icon density="compact" size="default" variant="text"
                    @click="resetState">
                    <v-icon size="default">mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <div class="flex-grow-1 d-flex flex-column overflow-hidden">
                <div v-if="showSearch" class="search-container pa-2 flex-shrink-0">
                    <rb-input v-model="searchQuery" prepend-inner-icon="mdi-magnify" placeholder="Pesquisar"
                        hide-details @update:model-value="debounceSearch"></rb-input>
                </div>
                <div v-if="isCheckboxMode" class="sticky-header flex-shrink-0">
                    <v-checkbox class="px-4" @click="toggleSelectAll" hide-details v-model="isAllSelected"
                        label="Todos"></v-checkbox>
                    <v-divider></v-divider>
                </div>
                <div class="content-container flex-grow-1 overflow-auto" @scroll="loadOnScroll">
                    <v-list>
                        <v-list-item v-if="displayItems.length === 0" class="text-center">
                            <v-list-item-title>{{ emptyMessage }}</v-list-item-title>
                        </v-list-item>
                        <template v-else>
                            <div v-if="isListItemMode">
                                <v-list-item class="py-4" append-icon="mdi-menu-right"
                                    v-for="(item, index) in displayItems" :key="index" @click="selectItem(item)">
                                    {{ item.name }}
                                </v-list-item>
                            </div>
                            <template v-else-if="isCheckboxMode">
                                <v-checkbox class="px-4" hide-details v-for="item in displayItems" :key="item.id"
                                    v-model="selectedIds" :label="item.name"
                                    :value="item.id"></v-checkbox>
                            </template>
                        </template>
                    </v-list>
                </div>
            </div>
            <div v-if="isCheckboxMode" class="footer-container">
                <v-divider></v-divider>
                <v-card-actions class="d-flex justify-end pa-2">
                    <v-btn class="px-6 py-5 d-flex" color="primary-60" rounded="xl" variant="elevated" @click="saveSelection">
                        Salvar
                    </v-btn>
                </v-card-actions>
            </div>
        </v-card>
    </v-menu>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed, onMounted, shallowRef } from 'vue';
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    data: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },
    titulo: { type: String, default: 'Adicionar itens' },
    type: { type: String, default: 'checkbox' },
    search: { type: Boolean, default: true },
    notFound: { type: String, default: 'Nenhum item encontrado' }
});

const emit = defineEmits(['update:modelValue', 'update:selected']);
const isLoading = ref(false);
const searchQuery = ref('');
const searchTimer = ref(null);
const selectedIds = ref([]);
const initialSelection = ref([]);
const displayItems = shallowRef([]);
const currentPage = ref(1);
const pageSize = ref(50);
const allItems = shallowRef([]);
const visibleIds = ref(new Set());
const isOpen = computed(() => props.modelValue);
const title = computed(() => props.titulo);
const showSearch = computed(() => props.search);
const emptyMessage = computed(() => props.notFound);
const isCheckboxMode = computed(() => props.type === 'checkbox');
const isListItemMode = computed(() => props.type === 'list-item');
const isAllSelected = computed(() => {
    if (visibleIds.value.size === 0) return false;
    return Array.from(visibleIds.value).every(id => selectedIds.value.includes(id));
});

const initData = () => {
    if (!props.data?.length) return;

    allItems.value = props.data;
    currentPage.value = 1;
    loadFirstPage();
    updateVisibleIds();
}

const loadFirstPage = () => {
    displayItems.value = allItems.value.slice(0, pageSize.value);
}

const updateVisibleIds = () => {
    if (!searchQuery.value) {
        visibleIds.value = new Set(allItems.value.map(item => item.id));
        return;
    }

    const query = searchQuery.value.toLowerCase();
    const filteredItems = allItems.value.filter(item =>
        item && ((item.titulo?.toLowerCase().includes(query)) ||
            (item.name?.toLowerCase().includes(query)))
    );

    visibleIds.value = new Set(filteredItems.map(item => item.id));
}

const debounceSearch = () => {
    if (searchTimer.value) clearTimeout(searchTimer.value);

    searchTimer.value = setTimeout(() => {
        currentPage.value = 1;
        updateVisibleIds();

        if (!searchQuery.value) {
            loadFirstPage();
            return;
        }

        const query = searchQuery.value.toLowerCase();
        const results = allItems.value.filter(item =>
            item && ((item.titulo?.toLowerCase().includes(query)) ||
                (item.name?.toLowerCase().includes(query)))
        );

        displayItems.value = results.slice(0, pageSize.value);
    }, 300);
}

const loadMoreItems = () => {
    const start = currentPage.value * pageSize.value;
    const source = getFilteredItems();

    if (start >= source.length) return;

    const nextItems = source.slice(start, start + pageSize.value);
    if (!nextItems.length) return;

    currentPage.value++;
    displayItems.value = [...displayItems.value, ...nextItems];
}

const getFilteredItems = () => {
    if (!searchQuery.value) return allItems.value;

    const query = searchQuery.value.toLowerCase();
    return allItems.value.filter(item =>
        item && ((item.titulo?.toLowerCase().includes(query)) ||
            (item.name?.toLowerCase().includes(query)))
    );
}

const loadOnScroll = (event) => {
    const target = event.target;
    const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (remainingScroll < 50) {
        loadMoreItems();
    }
}

const selectItem = (item) => {
    selectedIds.value.push(item.id);
}

const saveSelection = () => {
    emit('update:modelValue', false);
    const selectedItems = allItems.value.filter(item => selectedIds.value.includes(item.id));
    emit('update:selected', selectedItems);
}

const resetState = () => {
    selectedIds.value = [...initialSelection.value];
    emit('update:modelValue', false);
    searchQuery.value = '';
    currentPage.value = 1;
    displayItems.value = allItems.value.slice(0, pageSize.value);
    updateVisibleIds();
}

const toggleSelectAll = () => {
    if (isAllSelected.value) {
        selectedIds.value = selectedIds.value.filter(id => !visibleIds.value.has(id));
        return;
    }

    const newSelection = [...selectedIds.value];
    visibleIds.value.forEach(id => {
        if (!newSelection.includes(id)) {
            newSelection.push(id);
        }
    });
    selectedIds.value = newSelection;
}

const syncSelectedItems = () => {
    if (!props.selected?.length) return;

    selectedIds.value = props.selected.map(item => item.id);
    initialSelection.value = [...selectedIds.value];
}

watch(() => props.selected, () => {
    if (props.selected) {
        selectedIds.value = props.selected.map(item => item.id);
        initialSelection.value = [...selectedIds.value];
    }
});

watch(() => props.data, () => {
    initData();
}, { immediate: true });

watch(() => searchQuery.value, () => {
    updateVisibleIds();
});

onMounted(() => {
    syncSelectedItems();
    initData();
});
</script>

<style scoped>
.header {
    color: rgb(var(--v-theme-neutral-35));
    background-color: rgb(var(--v-theme-neutral-95));
    font-family: Roboto;
    font-size: 16px;
    font-weight: 500;
}

.sticky-header {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 1;
}

.content-container {
    min-height: 0;
}

.footer-container {
    background-color: white;
}

.search-results-item:deep(.v-list-item__content) {
    align-self: start;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.select-popup-primary-list-item {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
